import React, { useContext } from "react";
import { CartContext } from "../Component/CartContext";
import productData from "../Datas/productData";
import "../css/cartpage.scss";
import { Link, useNavigate } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);

  const getProductById = (id) => {
    return productData.find((product) => product.id === id);
  };

  const calculateSubtotal = () => {
    let total = 0;
    cartItems.forEach((item) => {
      const product = getProductById(item.id);
      if (product) {
        const price = parseFloat(product.price);
        total += price * item.quantity;
      }
    });
    return total;
  };

  const handleUpdateQuantity = (item, newQuantity) => {
    if (newQuantity > 0) {
      updateQuantity(item.id, item.size, newQuantity, {
        color: item.color,
        metalType: item.metalType,
        customization: item.customization,
      });
    }
  };

  const handleRemoveItem = (item) => {
    removeFromCart(item.id, item.size, {
      color: item.color,
      metalType: item.metalType,
      customization: item.customization,
    });
  };

  const handleCheckout = () => {
    if (cartItems.length > 0) {
      navigate("/checkout");
    } else {
      alert("Your cart is empty.");
    }
  };

  const freeShippingThreshold = 50;
  const subtotal = calculateSubtotal();
  const isEligibleForFreeShipping = subtotal >= freeShippingThreshold;

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="prestige--v4 features--heading-small features--heading-uppercase template-index">
      <div className="PageContainer">
        <Header />

        <div className="cart-page">
          {isEligibleForFreeShipping && (
            <div
              style={{
                background: "#f0f8f0",
                padding: "16px",
                textAlign: "center",
                marginBottom: "30px",
                borderRadius: "4px",
                color: "#2e7d32",
                fontWeight: "600",
              }}
            >
              You are eligible for free shipping!
            </div>
          )}

          <div className="cart-container">
            {/* Left Side - Cart Items */}
            <div className="cart-items-section">
              <h1 className="cart-title">CART</h1>

              {cartItems.length === 0 ? (
                <div className="empty-cart">
                  <p>Your cart is currently empty.</p>
                  <Link
                    onClick={toTop}
                    to="/shop"
                    className="continue-shopping-btn"
                  >
                    CONTINUE SHOPPING
                  </Link>
                </div>
              ) : (
                <div className="cart-items-list">
                  {cartItems.map((item, index) => {
                    const product = getProductById(item.id);
                    if (!product) return null;

                    // Calculate item total price
                    const itemTotal = (
                      parseFloat(product.price) * item.quantity
                    ).toFixed(2);
                    const originalPrice = product.originalPrice
                      ? (
                          parseFloat(product.originalPrice) * item.quantity
                        ).toFixed(2)
                      : null;

                    return (
                      <div key={index} className="cart-item">
                        <div className="item-image">
                          <img src={product.images[0]} alt={product.name} />
                        </div>

                        <div className="item-details">
                          <h3 className="item-name">{product.name}</h3>

                          {/* Color Display */}
                          {item.color && (
                            <p className="item-color">
                              <strong>Color:</strong> {item.color}
                            </p>
                          )}

                          {/* Size Display */}
                          {item.size && (
                            <p className="item-size">
                              <strong>Size:</strong> {item.size}
                            </p>
                          )}

                          {/* Metal Type Display */}
                          {item.metalType && (
                            <p className="item-metal">
                              <strong>Metal:</strong> {item.metalType}
                            </p>
                          )}

                          {/* Customization Display */}
                          {item.customization && (
                            <p className="item-customization">
                              <strong>Customization:</strong>{" "}
                              {item.customization}
                            </p>
                          )}

                          {/* Ship Date Display */}
                          {item.shipDate && (
                            <p className="item-ship-date">
                              Ships By {item.shipDate}
                            </p>
                          )}

                          {/* Price Display */}
                          <div className="item-price-wrapper">
                            {originalPrice && (
                              <>
                                <span className="original-price">
                                  ${originalPrice}
                                </span>
                                <span className="item-price">${itemTotal}</span>
                                <span className="discount-badge">
                                  25% Off Custom Gifts: The Annual Custom Event
                                </span>
                              </>
                            )}
                            {!originalPrice && (
                              <span className="item-price">${itemTotal}</span>
                            )}
                          </div>

                          {/* Sale Notice */}
                          {product.saleNote && (
                            <p className="sale-note">{product.saleNote}</p>
                          )}

                          {/* Quantity Controls and Remove Button */}
                          <div className="item-actions">
                            <div className="quantity-control">
                              <button
                                className="qty-btn"
                                onClick={() =>
                                  handleUpdateQuantity(item, item.quantity - 1)
                                }
                                disabled={item.quantity <= 1}
                              >
                                −
                              </button>
                              <span className="quantity">{item.quantity}</span>
                              <button
                                className="qty-btn"
                                onClick={() =>
                                  handleUpdateQuantity(item, item.quantity + 1)
                                }
                              >
                                +
                              </button>
                            </div>

                            <button
                              className="remove-btn"
                              onClick={() => handleRemoveItem(item)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right Side - Order Summary */}
            {cartItems.length > 0 && (
              <div className="order-summary-section">
                <h2 className="summary-title">ORDER SUMMARY</h2>

                {/* Contact Info */}
                <div className="summary-contact">
                  <p>
                    Questions? Call us at{" "}
                    <a href="tel:1-888-456-2899">1-888-456-2899</a> or email{" "}
                    <a href="mailto:vert@bakimball.world">
                      vert@bakimball.world
                    </a>
                  </p>
                </div>

                {/* Summary Details */}
                <div className="summary-details">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span className="amount">
                      ${calculateSubtotal().toFixed(2)}
                    </span>
                  </div>

                  <div className="summary-row">
                    <span>Shipping</span>
                    <span className="amount">
                      {isEligibleForFreeShipping
                        ? "FREE"
                        : "Calculated at checkout"}
                    </span>
                  </div>

                  <div className="summary-row total">
                    <span>TOTAL</span>
                    <span className="amount">
                      ${calculateSubtotal().toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Checkout Button */}
                <button className="checkout-btn" onClick={handleCheckout}>
                  CHECKOUT
                </button>

                {/* Sale Notice */}
                <p className="sale-notice">
                  *Final sale - cannot be canceled or altered
                </p>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default CartPage;
